import React, { memo, useEffect, useState } from 'react'
import { INumberInputSchema } from '../../../../../types'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { numberInputErrors } from '../../../../../../../utils/validationErrors/numberInputErrors'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import { buildControlsExt, doubleInput } from '../../../../../../inputs/controls'
import { initInputData } from '../../../../../../../utils/InitInputData'

export interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: INumberInputSchema
    name: string
    wrapper?: string
}

export const RuleDouble = memo(function RuleDouble({
    dataPath,
    dataSchema,
    name,
    wrapper,
    pathWithElementID,
}: Props) {
    const dispatch = useAppDispatch()
    const pathSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const path = [...pathSplit, name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        precision = 8,
        default: defaultValue,
        change_warning: changeWarning,
    } = dataSchema
    const validationCheck = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationCheck })
    const label: string = getLabel(name, inputLabel)
    const className: string = !wrapper ? 'w-50' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID?.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const errMsg = numberInputErrors(inputState?.[name], dataSchema)
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched && setTouched({ ...touched, [name]: true })
        if (inputState[name] === '') {
            setInputState((prev: any) => ({ ...prev, [name]: null }))
        }
    }

    const controlDescriptors = [
        doubleInput({
            name,
            tooltipText,
            label,
            precision,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            placeholder: defaultValue,
            changeWarning,
            onBlur: handleOnBlur,
        }),
    ]

    const DoubleInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
    )

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
    }, [touched[name], isChecked]) // eslint-disable-line

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{DoubleInput}</div>
})
